import { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import Link from "next/link";

import DocsLayout from "@/components/docs/DocsLayout";
import EndpointCard from "@/components/docs/EndpointCard";
import GettingStartedGuide from "@/components/docs/GettingStartedGuide";
import MethodBadge from "@/components/docs/MethodBadge";
import OpenApiGuide from "@/components/docs/OpenApiGuide";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import JsonLd from "@/components/seo/JsonLd";
import RelatedPages from "@/components/marketing/RelatedPages";
import FAQ from "@/components/shared/FAQ";
import Footer from "@/components/shared/Footer";
import FancyButton from "@/components/ui/fancy-button";
import { API, endpointsInGroup, getGroup } from "@/lib/docs/api";
import { DOC_PAGES, docPath, getDocPage, type DocPage } from "@/lib/docs/content";
import { breadcrumbsForPath } from "@/lib/seo/breadcrumbs";
import { pageGraph } from "@/lib/seo/graph";

interface DocPageProps {
  page: DocPage | null;
}

const GroupBody = ({ page }: { page: DocPage }) => {
  const group = page.group ? getGroup(page.group) : undefined;
  const endpoints = page.group ? endpointsInGroup(page.group) : [];

  if (!group) return null;

  return (
    <>
      {group.section === "view-tracker" && (
        <p className="rounded-xl border border-[#EFF2FF] bg-[#F9FBFF] px-5 py-4 text-[14px] leading-[1.65] text-[#525866]">
          {API.viewTracker.chooser}
        </p>
      )}
      <nav aria-label="Endpoints in this group" className="flex flex-col gap-2">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#8B93A7]">
          {endpoints.length} endpoints
        </span>
        <ul className="flex flex-col divide-y divide-[#EFF2FF] rounded-xl border border-[#EFF2FF]">
          {endpoints.map((endpoint) => (
            <li key={endpoint.id}>
              <a
                href={`#${endpoint.id}`}
                className="flex flex-col gap-1 px-4 py-2.5 transition-colors hover:bg-[#F9FBFF] sm:flex-row sm:items-center sm:gap-3"
              >
                <span className="flex items-center gap-2">
                  <MethodBadge method={endpoint.method} />
                  <code className="font-mono text-[12.5px] text-[#101828]">{endpoint.path}</code>
                </span>
                <span className="text-[13px] text-[#525866] sm:ml-auto sm:text-right">
                  {endpoint.title}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </nav>
      <div className="flex flex-col gap-10">
        {endpoints.map((endpoint) => (
          <EndpointCard key={endpoint.id} endpoint={endpoint} />
        ))}
      </div>
    </>
  );
};

const DocSlugPage = ({ page: initialPage }: DocPageProps) => {
  const router = useRouter();
  const slug = typeof router.query.slug === "string" ? router.query.slug : "";
  const page = initialPage ?? (slug ? (getDocPage(slug) ?? null) : null);

  if (!page) {
    if (!router.isReady) return null;

    return (
      <div className="flex min-h-[75vh] flex-col items-center justify-center">
        <h1 className="text-4xl font-bold text-gray-900">Page Not Found</h1>
        <p className="mt-4 text-lg text-gray-600">That API docs page does not exist.</p>
        <FancyButton
          href={docPath("")}
          semiFancy
          className="mt-8 w-fit rounded-lg bg-blue-600 px-6 py-3 text-white hover:bg-blue-700"
        >
          Browse the API docs
        </FancyButton>
      </div>
    );
  }

  const path = docPath(page.slug);
  const crumbs = breadcrumbsForPath(path);
  const eyebrow = page.kind === "group" ? "Endpoint reference" : "Guide";

  return (
    <div className="flex size-full flex-col overflow-hidden bg-white">
      <NextSeo title={`${page.seoTitle} | Crayo`} description={page.seoDescription} />
      <JsonLd
        nodes={pageGraph({
          path,
          name: page.seoTitle,
          description: page.seoDescription,
          crumbs,
          faqs: page.faqs,
          speakable: [".answer-first"],
        })}
      />
      <Breadcrumbs items={crumbs} />

      <DocsLayout current={page.slug} eyebrow={eyebrow} title={page.title} answer={page.answer}>
        {page.kind === "group" && <GroupBody page={page} />}
        {page.slug === "getting-started" && <GettingStartedGuide />}
        {page.slug === "openapi" && <OpenApiGuide />}

        <Link
          href="/features/api"
          prefetch={false}
          className="flex flex-col gap-1 rounded-2xl border border-[#CACFD8] bg-[#F9FBFF] px-5 py-4 transition-colors hover:border-[#335CFF] sm:px-8"
        >
          <span className="text-[15px] font-medium text-[#101828]">
            New to the Crayo API? See what it is for
          </span>
          <span className="text-[13px] text-[#525866]">
            Use cases, pricing and the console walkthrough live on the API feature page.
          </span>
        </Link>
      </DocsLayout>

      <RelatedPages path={path} />
      <FAQ items={page.faqs ?? []} />
      <Footer />
    </div>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: DOC_PAGES.map((page) => ({ params: { slug: page.slug } })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<DocPageProps> = async ({ params }) => {
  const slug = params?.slug as string;
  return { props: { page: getDocPage(slug) ?? null } };
};

export default DocSlugPage;
